import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Chip,
  Button,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { User } from '../../types/models';
import { getAllUsers } from '../../services/userService';
import { trackEvent, EventCategory, EventAction } from '../../services/analytics';
import { RecurrenceDeleteType } from '../../services/bookingService';

interface MyBookingsModalProps {
  open: boolean;
  onClose: () => void;
  bookings: Array<{
    id: string;
    title: string;
    start: Date;
    end: Date;
    extendedProps: {
      roomName: string;
      description?: string;
      attendee_uids: string[];
      userId?: string;
      recurrence_type?: string;
      parent_booking_id?: string;
    };
  }>;
  onCancel: (bookingId: string, recurrenceDeleteType?: RecurrenceDeleteType) => Promise<void>;
  onEdit?: (bookingId: string) => void;
  currentUser: User | null;
}

const MyBookingsModal: React.FC<MyBookingsModalProps> = ({
  open,
  onClose,
  bookings,
  onCancel,
  onEdit,
  currentUser
}) => {
  // 모든 사용자 정보 상태
  const [allUsers, setAllUsers] = useState<User[]>([]);

  // 삭제 다이얼로그 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [recurrenceDeleteType, setRecurrenceDeleteType] = useState<RecurrenceDeleteType>('THIS_EVENT');

  // 지난 예약 펼침/접힘 상태
  const [pastBookingsExpanded, setPastBookingsExpanded] = useState(false);

  // 모든 사용자 정보 가져오기
  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      try {
        const users = await getAllUsers();
        if (!cancelled) {
          setAllUsers(users);
          console.log('사용자 정보 로드 완료:', users.length);
        }
      } catch (error) {
        console.error('사용자 정보 로드 실패:', error);
      } finally {
        // no loading state
      }
    };

    // 사용자 목록은 모달을 열 때 한 번만 불러온다 (캐시가 있으면 캐시 사용).
    if (open) {
      fetchUsers();
    }

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Analytics: Track viewing my bookings when modal opens
  useEffect(() => {
    if (open && currentUser) {
      trackEvent(
        EventCategory.BOOKING,
        EventAction.VIEW_MY_BOOKINGS,
        currentUser.name
      );
    }
  }, [open, currentUser]);

  // UUID로 사용자 찾기
  const findUserById = (userId: string): User | undefined => {
    return allUsers.find((user) => user.id === userId);
  };

  // 수정 버튼 클릭
  const handleEditClick = (bookingId: string) => {
    if (onEdit) {
      onEdit(bookingId);
    }
  };

  // 삭제 버튼 클릭
  const handleDeleteClick = (booking: typeof bookings[0]) => {
    setSelectedBookingId(booking.id);

    // 반복 예약이면 다이얼로그 표시
    if (booking.extendedProps.recurrence_type && booking.extendedProps.recurrence_type !== 'NONE') {
      setDeleteDialogOpen(true);
    } else {
      // 단일 예약은 바로 삭제
      handleConfirmDelete();
    }
  };

  // 삭제 확인
  const handleConfirmDelete = async () => {
    if (selectedBookingId) {
      await onCancel(selectedBookingId, recurrenceDeleteType);
      setDeleteDialogOpen(false);
      setSelectedBookingId(null);
      setRecurrenceDeleteType('THIS_EVENT');
    }
  };

  // 예약을 과거/미래로 분류
  const now = new Date();
  const upcomingBookings = bookings.filter(booking => new Date(booking.start) > now);
  const pastBookings = bookings.filter(booking => new Date(booking.start) <= now);

  // 정렬
  upcomingBookings.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  pastBookings.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());

  // 배지 생성 함수
  const getBadges = (booking: typeof bookings[0], isPast: boolean) => {
    const badges = [];
    const startDate = dayjs(booking.start);
    const today = dayjs();
    const isToday = startDate.isSame(today, 'day');
    const hoursDiff = startDate.diff(today, 'hour');

    // 오늘 배지
    if (isToday && !isPast) {
      badges.push({ label: '오늘', color: 'primary' as const });
    }

    // 임박 배지 (24시간 이내)
    if (!isPast && hoursDiff <= 24 && hoursDiff > 0) {
      badges.push({ label: '임박', color: 'warning' as const });
    }

    // 반복 예약 배지
    if (booking.extendedProps.recurrence_type && booking.extendedProps.recurrence_type !== 'NONE') {
      badges.push({ label: '반복', color: 'default' as const });
    }

    return badges;
  };

  // 참석자 텍스트 생성
  const getAttendeesText = (booking: typeof bookings[0]) => {
    const attendeeIds = booking.extendedProps.attendee_uids || [];
    if (attendeeIds.length === 0) return null;

    const attendeeNames = attendeeIds
      .map(id => {
        const user = findUserById(id);
        if (!user) return null;
        return user.team ? `${user.name}(${user.team})` : user.name;
      })
      .filter(Boolean);

    if (attendeeNames.length === 0) return null;
    if (attendeeNames.length <= 3) {
      return `참석: ${attendeeNames.join(', ')}`;
    }
    return `참석: ${attendeeNames.slice(0, 2).join(', ')} 외 ${attendeeNames.length - 2}명`;
  };

  // 카드 렌더링
  const renderBookingCard = (booking: typeof bookings[0], isPast: boolean) => {
    const badges = getBadges(booking, isPast);
    const attendeesText = getAttendeesText(booking);
    const isHost = currentUser && booking.extendedProps.userId === currentUser.id;

    return (
      <Box
        key={booking.id}
        sx={{
          background: '#fff',
          border: '1px solid #e2e2e2',
          borderRadius: '10px',
          padding: '10px',
          opacity: isPast ? 0.65 : 1,
        }}
      >
        {/* 상단: 날짜/시간/장소 + 배지 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.4 }}>
            {dayjs(booking.start).format('YYYY-MM-DD (ddd) HH:mm')} ~ {dayjs(booking.end).format('HH:mm')} · {booking.extendedProps.roomName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexShrink: 0 }}>
            {badges.map((badge, idx) => (
              <Chip
                key={idx}
                label={badge.label}
                size="small"
                color={badge.color}
                variant="outlined"
                sx={{
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: 500,
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* 제목 */}
        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.4, mb: attendeesText ? 0.25 : 0.75 }}>
          {booking.title}
        </Typography>

        {/* 참석자 */}
        {attendeesText && (
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666', mb: 0.75 }}>
            {attendeesText}
          </Typography>
        )}

        {/* 버튼 */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
          {/* 상세 버튼 - 모두에게 표시 */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleEditClick(booking.id)}
            sx={{
              fontSize: '11px',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              textTransform: 'none',
              borderColor: '#d6d6d6',
              minWidth: 'auto',
            }}
          >
            상세
          </Button>

          {/* 취소 버튼 - 주최자 + 예정된 예약만 */}
          {!isPast && isHost && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleDeleteClick(booking)}
              sx={{
                fontSize: '11px',
                px: 1,
                py: 0.25,
                borderRadius: '4px',
                textTransform: 'none',
                borderColor: '#e0b4b4',
                minWidth: 'auto',
              }}
            >
              취소
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            내 예약 목록
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3, pb: 3 }}>
          {bookings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              예약된 회의가 없습니다.
            </Typography>
          ) : (
            <>
              {/* 예정된 예약 */}
              <Box sx={{ mb: 3.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1.25,
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#333',
                  }}
                >
                  예정된 예약
                </Typography>
                {upcomingBookings.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      display: 'grid',
                      gap: 1.5,
                    }}
                  >
                    {upcomingBookings.map((booking) => renderBookingCard(booking, false))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: '13px' }}>
                    예정된 예약이 없습니다.
                  </Typography>
                )}
              </Box>

              {/* 지난 예약 */}
              {pastBookings.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    onClick={() => setPastBookingsExpanded(!pastBookingsExpanded)}
                    sx={{
                      mb: 1.25,
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#333',
                      cursor: 'pointer',
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderRadius: '4px',
                        px: 0.5,
                        mx: -0.5,
                      }
                    }}
                  >
                    <span style={{ fontSize: '12px' }}>{pastBookingsExpanded ? '▼' : '▶'}</span>
                    지난 예약
                  </Typography>
                  {pastBookingsExpanded && (
                    <Box
                      sx={{
                        maxHeight: '300px',
                        overflow: 'auto',
                        display: 'grid',
                        gap: 1.5,
                      }}
                    >
                      {pastBookings.map((booking) => renderBookingCard(booking, true))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 반복 예약 삭제 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>반복 예약 삭제</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            이 예약은 반복 예약입니다. 어떻게 삭제하시겠습니까?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={recurrenceDeleteType}
              onChange={(e) => setRecurrenceDeleteType(e.target.value as RecurrenceDeleteType)}
            >
              <FormControlLabel
                value="THIS_EVENT"
                control={<Radio />}
                label="이 일정만 삭제"
              />
              <FormControlLabel
                value="THIS_AND_FOLLOWING"
                control={<Radio />}
                label="이 일정 및 향후 일정 삭제"
              />
              <FormControlLabel
                value="ALL_EVENTS"
                control={<Radio />}
                label="모든 반복 일정 삭제"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyBookingsModal;
